/**
 * Show loading spinner during token exchange and redirect
 */
function showLoadingSpinner() {
  // Check if loader already exists
  if (document.getElementById("avetta-deep-link-loader")) {
    return;
  }

  const loader = document.createElement("div");
  loader.id = "avetta-deep-link-loader";
  loader.className = "loader visible";
  loader.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--green-950, #10231f);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
  `;

  const spinner = document.createElement("div");
  spinner.className = "icon avetta-spinner";

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 260.4 202");

  const title = document.createElementNS("http://www.w3.org/2000/svg", "title");
  title.textContent = "loading";

  svg.appendChild(title);

  // Polygon points mirror Views.Spinners.avettaSpinner (2026 brandmark).
  // SYNC: Brandmark polygon data is duplicated in:
  //   src/elm/Views/Spinners.elm
  //   src/js/iframe/filePreview.js
  // Update all three when geometry changes.
  const polygonPoints = [
    "69.63,94.38 65.13,200.98 100.83,200.98 86.74,67.81",
    "35.32,147.68 19.8,171.76 1.01,200.98 38.82,200.98 50.86,123.51",
    "111.26,30.65 127.1,200.98 172.39,200.98 130.22,1.32",
    "111.26,30.65 195.55,200.98 259.13,200.98 130.22,1.32",
    "19.8,171.76 1.01,200.98 38.82,200.98",
    "69.63,94.38 127.1,200.98 172.39,200.98 86.93,67.52",
    "35.32,147.68 65.13,200.98 100.83,200.98 51,123.29",
  ];
  for (const points of polygonPoints) {
    const polygon = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
    polygon.setAttribute("stroke", "var(--white, #FFFFFF)");
    polygon.setAttribute("fill", "none");
    polygon.setAttribute("points", points);
    svg.appendChild(polygon);
  }

  spinner.appendChild(svg);
  loader.appendChild(spinner);

  document.body.appendChild(loader);
}

/**
 * Hide loading spinner
 * Exported so it can be called from other modules if needed
 */
export function hideLoadingSpinner() {
  const loader = document.getElementById("avetta-deep-link-loader");
  if (loader) {
    loader.remove();
  }
}

/**
 * Utility for exchanging one-time tokens from mobile app deep links
 * @param {Object} options - Configuration options
 * @param {string} options.apiBaseUrl - Base API URL (e.g., AVT.CONFIG.api or CONFIG.api)
 *   Used to extract environment for constructing the exchange URL
 * @param {Function} options.decodeToken - Function to decode JWT token
 * @param {Function} options.onSuccess - Callback when token exchange succeeds
 *   Receives: { authData, decodedToken }
 * @param {Function} options.onError - Callback when token exchange fails
 *   Receives: { error, isExpired, isNetworkError }
 * @param {string} [options.token] - Optional token to use directly (if not provided, will check URL)
 * @returns {Promise<void>} - Promise that resolves when token exchange completes
 */
export async function exchangeOneTimeToken({ apiBaseUrl, decodeToken, onSuccess, onError, token }) {
  // If token is provided directly, use it; otherwise check URL parameters
  let oneTimeToken = token;
  
  if (!oneTimeToken) {
    const urlParams = new URLSearchParams(window.location.search);
    oneTimeToken = urlParams.get("pass") || urlParams.get("token") || urlParams.get("oneTimeToken");
    
    // Also check inside referral parameter if token not found in direct params
    if (!oneTimeToken) {
      const referralParam = urlParams.get("referral");
      if (referralParam) {
        try {
          const referralUrl = new URL(referralParam);
          const referralParams = new URLSearchParams(referralUrl.search);
          oneTimeToken = referralParams.get("pass") || referralParams.get("token") || referralParams.get("oneTimeToken");
        } catch (e) {
          // Ignore parsing errors
        }
      }
    }
  }

  // If no token found, don't show spinner and return early
  if (!oneTimeToken) {
    return false;
  }

  // Show loading spinner when token exchange starts
  showLoadingSpinner();

  // Construct the exchange URL from apiBaseUrl (AVT.CONFIG.api / CONFIG.api)
  const exchangeUrl = `${apiBaseUrl}/connect-platform-gateway/v1/users/one-time-tokens/exchange`;

  try {
    const response = await fetch(exchangeUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // No Authorization header - this endpoint doesn't require authentication
      },
      body: JSON.stringify({
        oneTimeToken: oneTimeToken,
      }),
    });

    if (response.ok) {
      const authData = await response.json();
      
      // Log successful mobile app authentication
      if (window.analytics && window.analytics.track) {
        window.analytics.track("Mobile App Deep Link - Authentication Success", {
          source: "mobile_app",
          timestamp: Date.now(),
        });
      }

      const decodedToken = decodeToken(authData.access_token);
      
      // Keep spinner visible during redirect - it will be removed when page loads
      // Call onSuccess synchronously before resolving promise
      onSuccess({ authData, decodedToken });
      // Return true to indicate token was processed and redirect happened
      return true;
    } else {
      // Try to get error message from response
      let errorMessage = `Exchange failed with status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (e) {
        // If response is not JSON, ignore
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      throw error;
    }
  } catch (error) {
    // Log failed mobile app authentication
    if (window.analytics && window.analytics.track) {
      window.analytics.track("Mobile App Deep Link - Authentication Failed", {
        source: "mobile_app",
        error: error.message,
        status: error.status,
        timestamp: Date.now(),
      });
    }

    const isExpired = error.status === 400 || error.status === 401;
    const isNetworkError = !error.status;
    
    // Hide spinner on error before redirecting to login
    hideLoadingSpinner();
    
    onError({ error, isExpired, isNetworkError });
    // Return true to indicate token processing was attempted (even if failed)
    return true;
  }
}

/**
 * Helper to clean URL by removing token parameters
 * @param {string} url - URL to clean
 * @returns {URL} - Cleaned URL object
 */
export function cleanTokenFromUrl(url) {
  const cleanUrl = new URL(url);
  cleanUrl.searchParams.delete("pass");
  cleanUrl.searchParams.delete("token");
  cleanUrl.searchParams.delete("oneTimeToken");
  return cleanUrl;
}

/**
 * Helper to redirect to login with error message
 * @param {string} currentUrl - Current page URL
 */
export function redirectToLoginWithError(currentUrl) {
  // Prevent redirect loops - if we're already on login with error, don't redirect again
  const currentUrlObj = new URL(currentUrl);
  if (currentUrlObj.pathname === "/login" && currentUrlObj.searchParams.has("error")) {
    hideLoadingSpinner();
    return;
  }

  // Keep spinner visible during redirect to login
  const cleanUrl = cleanTokenFromUrl(currentUrl);
  const referral = cleanUrl.pathname === "/" ? "" : `?referral=${encodeURIComponent(cleanUrl.toString())}`;
  window.location = `/login${referral}${referral ? "&" : "?"}error=sessionExpired`;
}
